import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Maximize2, Minimize2, Save, Download, Upload, AlertCircle } from 'lucide-react';
import { formatDateTime } from '../utils/timeUtils';

// Scratch 编辑器 iframe 路径（同域子路径部署）
const SCRATCH_EDITOR_URL = '/scratch-editor/index.html';

// postMessage 消息类型定义
type EditorMessageType =
  | 'EDITOR_READY'
  | 'PROJECT_SAVED'
  | 'GET_SB3_DATA'
  | 'SB3_DATA_RESPONSE'
  | 'LOAD_PROJECT'
  | 'SET_READONLY'
  | 'ERROR';

interface EditorMessage {
  type: EditorMessageType;
  data?: any;
  callbackId?: string;
}

export default function ScratchEditorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pendingCallbacks = useRef<Map<string, (data: any) => void>>(new Map());
  const editorReadyRef = useRef(false);

  // 从 URL 参数获取项目数据
  const projectId = searchParams.get('projectId');
  const mode = searchParams.get('mode') || 'edit'; // edit, view, fullscreen
  const sb3Url = searchParams.get('sb3Url');

  // 同步 editorReady 状态到 ref
  useEffect(() => {
    editorReadyRef.current = editorReady;
  }, [editorReady]);

  // 发送消息到编辑器 iframe
  const sendToEditor = useCallback((message: EditorMessage) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(message, window.location.origin);
    }
  }, []);

  // 带回调的消息发送（用于请求-响应模式）
  const sendToEditorWithCallback = useCallback((message: Omit<EditorMessage, 'callbackId'>): Promise<any> => {
    return new Promise((resolve, reject) => {
      const callbackId = `cb_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const timeout = setTimeout(() => {
        pendingCallbacks.current.delete(callbackId);
        reject(new Error('编辑器响应超时'));
      }, 10000);

      pendingCallbacks.current.set(callbackId, (data) => {
        clearTimeout(timeout);
        pendingCallbacks.current.delete(callbackId);
        resolve(data);
      });

      sendToEditor({ ...message, callbackId });
    });
  }, [sendToEditor]);

  // 处理来自编辑器的消息
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // 仅接受同源消息
      if (event.origin !== window.location.origin) return;

      const message = event.data as EditorMessage;
      if (!message?.type) return;

      switch (message.type) {
        case 'EDITOR_READY':
          setEditorReady(true);
          setLoading(false);
          // 编辑器就绪后，如果有项目需要加载，则发送加载指令
          if (sb3Url) {
            sendToEditor({ type: 'LOAD_PROJECT', data: { projectId, sb3Url } });
          }
          if (mode === 'view') {
            sendToEditor({ type: 'SET_READONLY', data: { readonly: true } });
          }
          break;

        case 'PROJECT_SAVED':
          setLastSaved(new Date());
          break;

        case 'SB3_DATA_RESPONSE':
          if (message.callbackId && pendingCallbacks.current.has(message.callbackId)) {
            pendingCallbacks.current.get(message.callbackId)?.(message.data);
          }
          break;

        case 'ERROR':
          // 静默处理错误
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [projectId, sb3Url, mode, sendToEditor]);

  // 获取当前项目的 .sb3 数据
  const getSb3Data = async (): Promise<Blob | null> => {
    try {
      const data = await sendToEditorWithCallback({ type: 'GET_SB3_DATA' });
      return data?.sb3Blob || null;
    } catch (err) {
      return null;
    }
  };

  // 下载 .sb3 文件
  const handleDownload = async () => {
    const blob = await getSb3Data();
    if (!blob) {
      alert('无法获取项目数据');
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scratch_project_${projectId || Date.now()}.sb3`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // 上传 .sb3 文件
  const handleUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.sb3';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const url = URL.createObjectURL(file);
      sendToEditor({ type: 'LOAD_PROJECT', data: { sb3Url: url, fileName: file.name } });
    };
    input.click();
  };

  // 全屏切换
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // iframe 加载完成处理
  // 原生 Scratch 编辑器不会发送 EDITOR_READY postMessage，
  // 所以 iframe onLoad 触发后直接标记为就绪（不再依赖 postMessage）
  // AudioContext Monkey-patch 已在 scratch-editor/index.html 中内联注入，
  // 在 gui.js (defer) 加载之前执行，确保拦截所有 AudioContext 创建
  const handleIframeLoad = () => {
    setLoading(false);
    setEditorReady(true);
  };

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'} bg-gray-50 flex flex-col`}>
      {/* 顶部导航栏 */}
      <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          {!isFullscreen && (
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}
          <h1 className="text-lg font-semibold text-gray-900">Scratch 编程编辑器</h1>
          {lastSaved && (
            <span className="text-xs text-green-600 flex items-center gap-1">
              <Save className="w-3 h-3" />
              已保存 {formatDateTime(lastSaved.toISOString())}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {mode === 'edit' && editorReady && (
            <>
              <button
                onClick={handleUpload}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="导入 .sb3 文件"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">导入</span>
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="导出 .sb3 文件"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">导出</span>
              </button>
            </>
          )}
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={isFullscreen ? '退出全屏' : '全屏模式'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-5 h-5 text-gray-600" />
            ) : (
              <Maximize2 className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>
      </div>

      {/* 加载状态 */}
      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">正在加载 Scratch 编辑器...</p>
            <p className="text-gray-400 text-sm mt-2">首次加载可能需要几秒钟</p>
          </div>
        </div>
      )}

      {/* 错误状态 */}
      {error && !loading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-8">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">编辑器加载失败</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                重新加载
              </button>
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                返回
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iframe 容器 */}
      {!error && (
        <div className={`flex-1 relative ${loading ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <iframe
            ref={iframeRef}
            src={SCRATCH_EDITOR_URL}
            className="w-full h-full border-0"
            style={{ minHeight: isFullscreen ? '100%' : 'calc(100vh - 52px)' }}
            title="Scratch 编程编辑器"
            onLoad={handleIframeLoad}
            allow="camera; microphone"
          />
        </div>
      )}
    </div>
  );
}